import { useState, useEffect, useMemo } from 'react';
import { Download, FileText, Clock, Loader2, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import PortalLayout from '../shared/components/PortalLayout';
import EmployeeFilter from '../components/shared/EmployeeFilter';
import SearchableSelect from '../shared/components/SearchableSelect';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, navyBtn, outlineBtn, fieldStyle,
} from '../styles/tokens';
import client from '../api/client';
import { PAYMENT_METHODS } from '@shared/constants';

const tabs = [
  { key: 'expenses', label: 'Gastos Aprobados', icon: FileText },
  { key: 'nomina',   label: 'Nómina Aprobada',  icon: FileText },
  { key: 'exports',  label: 'Exportaciones',     icon: Download },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AccountingPortal() {
  const [page, setPage] = useState('expenses');

  return (
    <PortalLayout activeModule="contabilidad">
      {/* Tab bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 0, padding: '0 20px', alignItems: 'stretch',
      }}>
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setPage(key)} style={{
            padding: '12px 20px', fontSize: 13,
            fontWeight: page === key ? '700' : '500',
            color: page === key ? AMBER : '#64748b',
            borderBottom: page === key ? `2px solid ${AMBER}` : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {page === 'expenses' && <ApprovedExpenses />}
      {page === 'nomina' && <ApprovedNomina />}
      {page === 'exports' && <ExportHistory />}
    </PortalLayout>
  );
}

const CREDIT_CARD_OPTIONS = [
  { value: 'amex', label: 'Amex' },
  { value: 'home_depot', label: 'Home Depot' },
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'total', label: 'Total' },
  { value: 'visa', label: 'Visa' },
];
const USER_ROLE_OPTIONS = [
  { value: 'employee', label: 'Chofer' },
  { value: 'supervisor', label: 'Supervisor(a)' },
  { value: 'engineer', label: 'Ingeniero(a)' },
  { value: 'manager', label: 'Gerente' },
  { value: 'reviewer', label: 'Revisión Final' },
];

/* ── Approved Expenses ─────────────────────────────────────────────────── */

function ApprovedExpenses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeExported, setIncludeExported] = useState(false);
  const [paidFilter, setPaidFilter] = useState('unpaid');
  const [expandedReceipts, setExpandedReceipts] = useState(new Set());

  // Filters — default to no date range (show all unpaid)
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [creditCard, setCreditCard] = useState('');
  const [projectId, setProjectId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userId, setUserId] = useState('');
  const [projects, setProjects] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  // V32.4: Sage Vendor catalog for inline assignment on legacy receipts that predate
  // the requirement. Two lists; dropdown picks one by the receipt's payment method.
  const [creditCardVendors, setCreditCardVendors] = useState([]);
  const [pettyCashVendors, setPettyCashVendors] = useState([]);
  const [savingVendorFor, setSavingVendorFor] = useState(null);

  useEffect(() => {
    client.get('/api/projects').then(r => setProjects(r.data?.filter(p => p.active !== false) || [])).catch(() => {});
    client.get('/api/vendor-mappings/active', { params: { category: 'credit_card' } })
      .then(r => setCreditCardVendors(r.data || [])).catch(() => {});
    client.get('/api/vendor-mappings/active', { params: { category: 'petty_cash' } })
      .then(r => setPettyCashVendors(r.data || [])).catch(() => {});
  }, []);

  async function handleAssignVendor(receiptId, vendorId) {
    setSavingVendorFor(receiptId);
    try {
      await client.patch(`/api/receipts/${receiptId}`, { sageVendorId: vendorId || '' });
      setItems(prev => prev.map(it => it.receiptId === receiptId ? { ...it, sageVendorId: vendorId || null } : it));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al asignar el Proveedor Sage.');
    } finally {
      setSavingVendorFor(null);
    }
  }

  useEffect(() => {
    if (!userRole) { setFilteredUsers([]); setUserId(''); return; }
    client.get('/api/accounting/users-by-role', { params: { role: userRole } })
      .then(r => setFilteredUsers(r.data || []))
      .catch(() => setFilteredUsers([]));
    setUserId('');
  }, [userRole]);

  function buildParams() {
    const params = {};
    if (from) params.from = from;
    if (to)   params.to   = to;
    if (paymentMethod) params.payment_method = paymentMethod;
    if (creditCard)    params.credit_card    = creditCard;
    if (projectId)     params.project_id     = projectId;
    if (userId)        params.user_id        = userId;
    if (userRole)      params.user_role      = userRole;
    return params;
  }

  function fetchItems() {
    setLoading(true);
    client.get('/api/accounting/receipts', { params: buildParams() })
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleTogglePaid(itemId, newPaidState) {
    try {
      const res = await client.patch(`/api/accounting/items/${itemId}/mark-paid`, { isPaid: newPaidState });
      setItems(prev => prev.map(it =>
        it.itemId === itemId ? { ...it, isPaid: res.data.isPaid, paidAt: res.data.paidAt } : it
      ));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar estado de pago');
    }
  }

  async function handleToggleReceiptPaid(receiptGroup, newPaidState) {
    try {
      await client.patch(`/api/accounting/receipts/${receiptGroup.receiptId}/mark-paid`, { isPaid: newPaidState });
      // Refresh items to get updated states
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar estado de pago');
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = buildParams();
      if (includeExported) params.includeExported = 'true';
      const res = await client.get('/api/accounting/exports/expenses', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'gastos_sage.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      fetchItems();
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'No hay items pagados nuevos para exportar. Marque "Incluir exportados" para re-exportar.'
        : 'Error al exportar: ' + (err.response?.data?.error || err.message);
      alert(msg);
    } finally {
      setExporting(false);
    }
  }

  const paidCount = items.filter(i => i.isPaid).length;
  const unpaidCount = items.filter(i => !i.isPaid).length;
  const filteredItems = paidFilter === 'paid' ? items.filter(i => i.isPaid)
    : paidFilter === 'unpaid' ? items.filter(i => !i.isPaid)
    : items;
  const total = filteredItems.reduce((s, i) => {
    const amt = parseFloat(i.amount) || 0;
    return s + amt + amt * (parseFloat(i.taxRate) || 0) / 100;
  }, 0);

  // Group items by receipt
  const receiptGroups = useMemo(() => {
    const map = {};
    for (const item of filteredItems) {
      const key = item.receiptId || item.receiptNumber || item.itemId;
      if (!map[key]) {
        map[key] = {
          receiptId: item.receiptId,
          receiptNumber: item.receiptNumber,
          vendor: item.vendor,
          employeeName: item.employeeName,
          receiptDate: item.receiptDate,
          paymentMethod: item.paymentMethod,
          invoiceNumber: item.invoiceNumber,
          sageVendorId: item.sageVendorId || null,
          items: [],
          totalAmount: 0,
          allPaid: true,
          anyExported: false,
        };
      }
      map[key].items.push(item);
      const amt = parseFloat(item.amount) || 0;
      map[key].totalAmount += amt + amt * (parseFloat(item.taxRate) || 0) / 100;
      if (!item.isPaid) map[key].allPaid = false;
      if (item.isExported) map[key].anyExported = true;
    }
    return Object.values(map);
  }, [filteredItems]);

  function toggleExpand(receiptId) {
    setExpandedReceipts(prev => {
      const next = new Set(prev);
      next.has(receiptId) ? next.delete(receiptId) : next.add(receiptId);
      return next;
    });
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 16px' }}>Gastos Aprobados</h2>

      {/* Filter bar */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Desde</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Hasta</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Método de pago</div>
          <select value={paymentMethod} onChange={e => { setPaymentMethod(e.target.value); if (e.target.value !== 'credit_card') setCreditCard(''); }}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }}>
            <option value="">Todos</option>
            {Object.entries(PAYMENT_METHODS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
        {paymentMethod === 'credit_card' && (
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Tarjeta</div>
            <select value={creditCard} onChange={e => setCreditCard(e.target.value)}
              style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }}>
              <option value="">Todas</option>
              {CREDIT_CARD_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        )}
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Proyecto</div>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 160 }}>
            <option value="">Todos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ''}{p.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Tipo de usuario</div>
          <select value={userRole} onChange={e => setUserRole(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 130 }}>
            <option value="">Todos</option>
            {USER_ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        {userRole && filteredUsers.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Nombre</div>
            <select value={userId} onChange={e => setUserId(e.target.value)}
              style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 160 }}>
              <option value="">Todos</option>
              {filteredUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        <button onClick={fetchItems} disabled={loading} style={{ ...navyBtn, padding: '7px 16px', fontSize: 12 }}>
          {loading ? 'Cargando…' : 'Buscar'}
        </button>
      </div>

      {/* Summary + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <strong style={{ color: NAVY }}>{receiptGroups.length}</strong> recibos ·{' '}
          <strong style={{ color: NAVY }}>{filteredItems.length}</strong> items ·{' '}
          <strong style={{ color: NAVY }}>{formatCurrency(total)}</strong> total ·{' '}
          {paidCount} pagados · {unpaidCount} pendientes
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {['all', 'unpaid', 'paid'].map(val => (
            <button key={val} onClick={() => setPaidFilter(val)} style={{
              padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: '600', cursor: 'pointer',
              border: `1px solid ${paidFilter === val ? AMBER : BORDER_COLOR}`,
              background: paidFilter === val ? '#fffbeb' : '#fff',
              color: paidFilter === val ? '#92400e' : '#64748b',
            }}>
              {val === 'all' ? 'Todos' : val === 'paid' ? 'Pagados' : 'Sin Pagar'}
            </button>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeExported} onChange={e => setIncludeExported(e.target.checked)} />
            Incluir exportados
          </label>
          <button onClick={handleExport} disabled={exporting || paidCount === 0} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            opacity: exporting || paidCount === 0 ? 0.5 : 1,
          }}>
            <Download size={14} /> {exporting ? 'Exportando…' : 'Exportar Pagados'}
          </button>
        </div>
      </div>

      <div style={bubbleStyle}>
        {/* Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '28px 50px 1fr 1fr 100px 100px 90px 80px 80px',
          gap: 6, padding: '0 0 12px', borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: 10, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span></span><span>#</span><span>Proveedor</span><span>Empleado</span>
          <span>Método</span><span>Fecha</span><span style={{ textAlign: 'right' }}>Total</span>
          <span style={{ textAlign: 'center' }}>Pagado</span><span></span>
        </div>

        {receiptGroups.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No hay recibos aprobados {paidFilter === 'paid' ? 'pagados' : paidFilter === 'unpaid' ? 'sin pagar' : ''}
          </div>
        )}

        {receiptGroups.map(group => {
          const isExpanded = expandedReceipts.has(group.receiptId);
          const ExpandIcon = isExpanded ? ChevronDown : ChevronRight;
          return (
            <div key={group.receiptId}>
              {/* Receipt summary row */}
              <div
                onClick={() => toggleExpand(group.receiptId)}
                style={{
                  display: 'grid', gridTemplateColumns: '28px 50px 1fr 1fr 100px 100px 90px 80px 80px',
                  gap: 6, padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
                  alignItems: 'center', fontSize: 13, cursor: 'pointer',
                  background: group.allPaid ? '#f0fdf4' : '#fff',
                }}
              >
                <ExpandIcon size={16} style={{ color: '#94a3b8' }} />
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{group.receiptNumber || '—'}</span>
                <div>
                  <div style={{ fontWeight: '600', color: NAVY, fontSize: 13 }}>{group.vendor || '—'}</div>
                  {group.invoiceNumber && <div style={{ fontSize: 11, color: '#94a3b8' }}>Fact: {group.invoiceNumber}</div>}
                  {!group.sageVendorId && !group.anyExported && (
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#92400e' }}>⚠ Sin Proveedor Sage</div>
                  )}
                </div>
                <span style={{ color: '#475569', fontSize: 12 }}>{group.employeeName || '—'}</span>
                <span style={{ color: '#475569', fontSize: 11 }}>
                  {group.paymentMethod ? (PAYMENT_METHODS[group.paymentMethod] || group.paymentMethod) : '—'}
                </span>
                <span style={{ color: '#475569', fontSize: 12 }}>{formatDate(group.receiptDate)}</span>
                <span style={{ textAlign: 'right', fontWeight: '700', color: NAVY }}>
                  {formatCurrency(group.totalAmount)}
                  <div style={{ fontSize: 10, fontWeight: '400', color: '#94a3b8' }}>{group.items.length} item{group.items.length !== 1 ? 's' : ''}</div>
                </span>
                <div style={{ textAlign: 'center' }}>
                  {group.allPaid ? (
                    <CheckCircle size={16} style={{ color: '#16a34a' }} />
                  ) : (
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>
                      {group.items.filter(i => i.isPaid).length}/{group.items.length}
                    </span>
                  )}
                </div>
                <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                  {group.anyExported && group.allPaid ? (
                    <span style={{
                      padding: '3px 8px', borderRadius: 6, fontSize: 9, fontWeight: '600',
                      background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                    }}>Exportado</span>
                  ) : (
                    <button
                      onClick={() => handleToggleReceiptPaid(group, !group.allPaid)}
                      style={{
                        padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: '600',
                        cursor: 'pointer', border: 'none',
                        background: group.allPaid ? '#fef2f2' : '#f0fdf4',
                        color: group.allPaid ? '#dc2626' : '#16a34a',
                      }}
                    >
                      {group.allPaid ? 'Desmarcar' : 'Pagar Todo'}
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded items — matches export columns */}
              {isExpanded && (
                <div style={{ background: '#f8fafc', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  {/* V32.4: Sage Vendor assignment. Legacy receipts approved before the
                      requirement have no vendor and won't export until one is assigned.
                      Locked once any item is exported. */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '8px 16px', borderBottom: `1px solid ${BORDER_COLOR}`,
                    background: (group.sageVendorId || group.anyExported) ? '#f8fafc' : '#fffbeb',
                  }} onClick={e => e.stopPropagation()}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap' }}>Proveedor Sage:</span>
                    {group.anyExported ? (
                      <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, fontFamily: 'monospace' }}>
                        ✓ {group.sageVendorId || '—'} (exportado, bloqueado)
                      </span>
                    ) : (() => {
                      const isPettyCash = group.paymentMethod === 'petty_cash' || group.paymentMethod === 'efectivo';
                      const vendorItems = ((isPettyCash ? pettyCashVendors : creditCardVendors) || [])
                        .map(v => ({ id: v.vendor_id, label: v.vendor_id, sublabel: v.vendor_name }));
                      return (
                        <>
                          <div style={{ width: 300 }}>
                            <SearchableSelect
                              items={vendorItems}
                              value={group.sageVendorId}
                              onChange={(vid) => handleAssignVendor(group.receiptId, vid)}
                              placeholder={isPettyCash ? 'Buscar PET-...' : 'Buscar proveedor...'}
                              disabled={savingVendorFor === group.receiptId}
                            />
                          </div>
                          {group.sageVendorId
                            ? <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>✓ {group.sageVendorId}</span>
                            : <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>⚠ Sin Proveedor Sage — no exportará</span>
                          }
                          {savingVendorFor === group.receiptId && <span style={{ fontSize: 11, color: '#94a3b8' }}>Guardando…</span>}
                          {group.sageVendorId && savingVendorFor !== group.receiptId && (
                            <button onClick={() => handleAssignVendor(group.receiptId, null)}
                              style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                              Quitar
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* Sub-header */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 90px 110px 90px 70px 35px 65px 45px 55px 65px 70px 70px',
                    gap: 4, padding: '6px 16px', fontSize: 9, fontWeight: 700, color: '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}>
                    <span>Descripción</span><span>Proyecto ID</span><span>Tarea</span><span>Centro Costo</span>
                    <span>GL</span>
                    <span style={{ textAlign: 'center' }}>Cant</span>
                    <span style={{ textAlign: 'right' }}>Monto</span>
                    <span style={{ textAlign: 'right' }}>Tax%</span>
                    <span style={{ textAlign: 'right' }}>Tax$</span>
                    <span style={{ textAlign: 'right' }}>Total</span>
                    <span style={{ textAlign: 'center' }}>Pagado</span><span></span>
                  </div>
                  {group.items.map(item => {
                    const amt = parseFloat(item.amount) || 0;
                    const taxAmt = amt * (parseFloat(item.taxRate) || 0) / 100;
                    return (
                    <div key={item.itemId} style={{
                      display: 'grid', gridTemplateColumns: '1fr 90px 110px 90px 70px 35px 65px 45px 55px 65px 70px 70px',
                      gap: 4, padding: '7px 16px', borderBottom: `1px solid #f1f5f9`,
                      alignItems: 'start', fontSize: 11,
                    }}>
                      <div style={{ minWidth: 0, wordBreak: 'break-word' }}>
                        <span style={{ fontWeight: '500', color: '#475569' }}>{item.description || '—'}</span>
                        {item.isPersonal && (
                          <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 12, padding: '1px 6px', fontSize: 9, fontWeight: 600, marginLeft: 6 }}>Personal</span>
                        )}
                      </div>
                      <span style={{ color: '#475569', fontSize: 10, wordBreak: 'break-word' }}>{item.projectCode || '—'}</span>
                      <span style={{ color: '#475569', fontSize: 10, wordBreak: 'break-word' }}>{item.selectedTaskName || '—'}</span>
                      <span style={{ color: '#475569', fontSize: 10, wordBreak: 'break-word' }}>{item.costCenter || '—'}</span>
                      <span style={{ color: '#475569', fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-word' }}>{item.glCode || '—'}</span>
                      <span style={{ textAlign: 'center', color: '#475569' }}>{item.quantity || 1}</span>
                      <span style={{ textAlign: 'right', fontWeight: '600', color: '#475569' }}>{formatCurrency(amt / (item.quantity || 1))}</span>
                      <span style={{ textAlign: 'right', color: '#94a3b8' }}>{item.taxRate > 0 ? `${item.taxRate}%` : '0%'}</span>
                      <span style={{ textAlign: 'right', color: '#94a3b8' }}>{taxAmt > 0 ? formatCurrency(taxAmt) : '—'}</span>
                      <span style={{ textAlign: 'right', fontWeight: '600', color: NAVY }}>{formatCurrency(amt + taxAmt)}</span>
                      <div style={{ textAlign: 'center' }}>
                        {item.isPaid ? <CheckCircle size={14} style={{ color: '#16a34a' }} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        {!(item.isPaid && item.isExported) && (
                          <button
                            onClick={() => handleTogglePaid(item.itemId, !item.isPaid)}
                            style={{
                              padding: '2px 8px', borderRadius: 6, fontSize: 9, fontWeight: '600',
                              cursor: 'pointer', border: 'none',
                              background: item.isPaid ? '#fef2f2' : '#f0fdf4',
                              color: item.isPaid ? '#dc2626' : '#16a34a',
                            }}
                          >
                            {item.isPaid ? 'Desmarcar' : 'Pagar'}
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Nómina Sheet List (Collapsible) ───────────────────────────────────── */

// V31: derive sheet-level export state from entry-level tracking returned by getApprovedSheets.
const SAGE_BADGE_CONFIG = {
  exported: { label: 'Exportado', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  partial:  { label: 'Parcial',   color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  pending:  { label: 'Pendiente', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
};

function getSheetExportStatus(sheet) {
  const total = Number(sheet.total_entries) || 0;
  const exported = Number(sheet.exported_entries) || 0;
  if (total === 0 || exported === 0) return 'pending';
  if (exported === total) return 'exported';
  return 'partial';
}

function NominaSheetList({ sheets }) {
  const [expanded, setExpanded] = useState({});

  function toggleSheet(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // Group entries by employee for display
  function groupByEmployee(entries) {
    const map = {};
    for (const e of (entries || [])) {
      const key = e.employee_id || e.employee_name || 'unknown';
      if (!map[key]) map[key] = { name: e.employee_name || '—', id: e.employee_id || '—', tasks: [] };
      map[key].tasks.push(e);
    }
    return Object.values(map);
  }

  return (
    <div style={bubbleStyle}>
      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '24px 1fr 100px 1fr 70px 70px 60px 70px 80px',
        gap: 6, padding: '0 0 10px', borderBottom: `1px solid ${BORDER_COLOR}`,
        fontSize: 10, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        <span></span>
        <span>Proyecto</span><span>Fecha</span><span>Supervisor</span>
        <span style={{ textAlign: 'right' }}>Regular</span>
        <span style={{ textAlign: 'right' }}>Extra</span>
        <span style={{ textAlign: 'center' }}>Empl.</span>
        <span>Estado</span>
        <span style={{ textAlign: 'center' }}>Sage</span>
      </div>

      {sheets.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          No hay tarjetas de nómina aprobadas pendientes de exportación
        </div>
      )}

      {sheets.map(s => {
        const isOpen = !!expanded[s.id];
        const empGroups = isOpen ? groupByEmployee(s.entries) : [];

        return (
          <div key={s.id} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
            {/* Sheet row — clickable */}
            <div
              onClick={() => toggleSheet(s.id)}
              style={{
                display: 'grid', gridTemplateColumns: '24px 1fr 100px 1fr 70px 70px 60px 70px 80px',
                gap: 6, padding: '10px 0', alignItems: 'center', fontSize: 13,
                cursor: 'pointer', transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isOpen ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#64748b" />}
              </span>
              <span style={{ fontWeight: '600', color: NAVY, fontSize: 12 }}>
                {s.project_code ? `${s.project_code} — ` : ''}{s.project_name || '—'}
              </span>
              <span style={{ color: '#475569', fontSize: 12 }}>{formatDate(s.sheet_date)}</span>
              <span style={{ color: '#475569', fontSize: 12 }}>{s.supervisor_name || '—'}</span>
              <span style={{ textAlign: 'right', fontWeight: '600', fontSize: 12 }}>{Number(s.total_regular || 0).toFixed(1)}h</span>
              <span style={{ textAlign: 'right', fontWeight: '600', color: AMBER, fontSize: 12 }}>{Number(s.total_overtime || 0).toFixed(1)}h</span>
              <span style={{ textAlign: 'center', fontSize: 12, color: '#475569' }}>{s.employee_count || '—'}</span>
              <span>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                  fontSize: 9, fontWeight: '600', background: '#f0fdf4', color: '#166534',
                }}>Aprobado</span>
              </span>
              <span style={{ textAlign: 'center' }}>
                {(() => {
                  const status = getSheetExportStatus(s);
                  const cfg = SAGE_BADGE_CONFIG[status];
                  const total = Number(s.total_entries) || 0;
                  const exported = Number(s.exported_entries) || 0;
                  return (
                    <span
                      title={status === 'partial' ? `${exported} de ${total} entries exportadas` : undefined}
                      style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                        fontSize: 9, fontWeight: '600',
                        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                      }}
                    >
                      {cfg.label}
                    </span>
                  );
                })()}
              </span>
            </div>

            {/* Expanded: entry details grouped by employee */}
            {isOpen && (
              <div style={{
                padding: '0 0 12px 32px',
                animation: 'fadeUp 0.15s ease',
              }}>
                {empGroups.map((emp, ei) => (
                  <div key={ei} style={{
                    marginBottom: ei < empGroups.length - 1 ? 10 : 0,
                    background: BG_FIELD, borderRadius: 8, padding: '8px 12px',
                  }}>
                    {/* Employee header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                      fontSize: 12, fontWeight: 600, color: NAVY,
                    }}>
                      <span style={{
                        background: '#dbeafe', color: '#1e40af', padding: '1px 6px',
                        borderRadius: 8, fontSize: 10, fontWeight: 700,
                      }}>ID: {emp.id}</span>
                      {emp.name}
                    </div>

                    {/* Task rows */}
                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1fr', gap: 4, fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>
                      <span>Tarea</span><span>Descripción</span><span>C. Costo</span>
                      <span style={{ textAlign: 'right' }}>Reg</span>
                      <span style={{ textAlign: 'right' }}>Extra</span>
                      <span>Notas</span>
                    </div>
                    {emp.tasks.map((t, ti) => {
                      // V31.1: preview mirrors the CSV's strict whitelist — only the literal
                      // '02-FRINGES' renders as fringe. Any other category value (including
                      // typos) shows as '01-LABOR' so accounting sees exactly what would land
                      // in Sage.
                      const isFringe = String(t.cost_code_category || '').trim() === '02-FRINGES';
                      const cc = isFringe ? '02-FRINGES' : '01-LABOR';
                      return (
                        <div key={ti} style={{
                          display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1fr',
                          gap: 4, fontSize: 12, padding: '3px 0',
                          borderTop: ti > 0 ? `1px solid ${BORDER_COLOR}` : 'none',
                        }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: NAVY }}>{t.cost_code || '—'}</span>
                          <span style={{ color: '#475569' }}>{t.cost_code_desc || '—'}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            color: isFringe ? '#7c3aed' : '#166534',
                          }}>{cc}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600 }}>{Number(t.regular_hours || 0).toFixed(1)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: AMBER }}>{Number(t.overtime_hours || 0).toFixed(1)}</span>
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>{t.notes || ''}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Approved Nómina ───────────────────────────────────────────────────── */

function ApprovedNomina() {
  const [sheets, setSheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeExported, setIncludeExported] = useState(false);

  // Filters
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [projectId, setProjectId] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]); // [] = all

  useEffect(() => {
    client.get('/api/projects').then(r => setProjects(r.data?.filter(p => p.active !== false) || [])).catch(() => {});
    client.get('/api/nomina/workers').then(r => setEmployees(r.data || [])).catch(() => {});
    fetchSheets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function fetchSheets() {
    setLoading(true);
    const params = {};
    if (from)      params.from       = from;
    if (to)        params.to         = to;
    if (projectId) params.project_id = projectId;
    client.get('/api/accounting/nomina', { params })
      .then(r => setSheets(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = {};
      if (includeExported) params.includeExported = 'true';
      if (from)      params.from       = from;
      if (to)        params.to         = to;
      if (projectId) params.project_id = projectId;
      if (selectedEmployees.length) params.employee_ids = selectedEmployees.join(',');
      const res = await client.get('/api/accounting/exports/nomina', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'nomina_sage.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      // V32.8: GL-coded hours are excluded from Sage until the GL→Account mapping is set.
      // Surface the count so the clerk knows those hours aren't in this file.
      const glExcluded = parseInt(res.headers['x-gl-excluded-count'] || '0', 10);
      if (glExcluded > 0) {
        alert(`Nota: ${glExcluded} entrada(s) codificada(s) a Cuentas GL no se incluyen en este archivo Sage — el mapeo GL→Sage está pendiente de Contabilidad.`);
      }
      // Refetch so the badges reflect the new sage_exported_at marks.
      fetchSheets();
    } catch (err) {
      // responseType is 'blob', so a JSON error body arrives as a Blob — read it back
      // to surface the server message (e.g. the GL-exclusion notice on a GL-only window).
      let serverMsg = '';
      if (err.response?.data instanceof Blob) {
        try { serverMsg = JSON.parse(await err.response.data.text()).error; } catch { /* not JSON */ }
      } else {
        serverMsg = err.response?.data?.error;
      }
      const msg = serverMsg
        ? serverMsg
        : (err.response?.status === 404
            ? 'No hay registros nuevos para exportar. Marque "Incluir exportados" para re-exportar.'
            : 'Error al exportar: ' + err.message);
      alert(msg);
    } finally {
      setExporting(false);
    }
  }

  const totalRegular  = sheets.reduce((s, r) => s + parseFloat(r.total_regular  || 0), 0);
  const totalOvertime = sheets.reduce((s, r) => s + parseFloat(r.total_overtime || 0), 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 16px' }}>Nómina Aprobada</h2>

      {/* Filter bar */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Desde</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Hasta</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Proyecto</div>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 180 }}>
            <option value="">Todos los proyectos</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ''}{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Empleados</div>
          <EmployeeFilter
            employees={employees}
            selected={selectedEmployees}
            onChange={setSelectedEmployees}
          />
        </div>
        <button onClick={fetchSheets} disabled={loading} style={{ ...navyBtn, padding: '7px 16px', fontSize: 12 }}>
          {loading ? 'Cargando…' : 'Buscar'}
        </button>
      </div>

      {/* Summary + export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <strong style={{ color: NAVY }}>{sheets.length}</strong> tarjetas ·{' '}
          <strong style={{ color: NAVY }}>{totalRegular.toFixed(1)}h</strong> regular ·{' '}
          <strong style={{ color: AMBER }}>{totalOvertime.toFixed(1)}h</strong> extra
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeExported} onChange={e => setIncludeExported(e.target.checked)} />
            Incluir exportados
          </label>
          <button onClick={handleExport} disabled={exporting || sheets.length === 0} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            opacity: exporting || sheets.length === 0 ? 0.5 : 1,
          }}>
            <Download size={14} /> {exporting ? 'Exportando…' : 'Exportar CSV (Sage)'}
          </button>
        </div>
      </div>

      <NominaSheetList sheets={sheets} />
    </div>
  );
}

/* ── Export History ─────────────────────────────────────────────────────── */

function ExportHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/accounting/exports/history')
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0, marginBottom: 20 }}>
        Historial de Exportaciones
      </h2>

      <div style={bubbleStyle}>
        <div style={{
          display: 'grid', gridTemplateColumns: '120px 1fr 100px 1fr',
          gap: 8, padding: '0 0 12px', borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span>Módulo</span><span>Archivo</span><span style={{ textAlign: 'right' }}>Registros</span><span>Exportado</span>
        </div>

        {history.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No hay exportaciones previas
          </div>
        )}

        {history.map(h => (
          <div key={h.id} style={{
            display: 'grid', gridTemplateColumns: '120px 1fr 100px 1fr',
            gap: 8, padding: '12px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            alignItems: 'center', fontSize: 13,
          }}>
            <span>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                fontSize: 10, fontWeight: '600',
                background: h.module === 'expenses' ? '#dbeafe' : '#fef3c7',
                color: h.module === 'expenses' ? '#1e40af' : '#92400e',
              }}>
                {h.module === 'expenses' ? 'Gastos' : 'Nómina'}
              </span>
            </span>
            {/* V31: nomina_entries rows track entry IDs in record_ids (vs nomina = sheet IDs) — display the same label. */}
            <span style={{ fontWeight: '500', color: NAVY }}>{h.file_name || '—'}</span>
            <span style={{ textAlign: 'right', fontWeight: '600' }}>{h.row_count}</span>
            <span style={{ color: '#475569' }}>
              {formatDate(h.exported_at)} — {h.exported_by_name || '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
      <Loader2 size={20} className="spin" /> Cargando…
    </div>
  );
}
